import {
  countBeadsOutsideWidth,
  countBeadsUsingColor,
  createPattern,
  DEFAULT_PATTERN_TYPE,
  createSession,
  defaultPalette,
  MAX_COLORS,
  MAX_ROWS,
  MIN_ROWS,
  readRow,
  systemClock,
  trackingStatus,
  uuidGenerator,
  type PatternDraft,
  type PatternRepository,
} from '@miyuki/domain';
import type { EntityId, HexColor, PatternSummaryDto, PatternType } from '@miyuki/types';
import { BeadGridCanvas, ScrollHint } from '@miyuki/ui';
import * as React from 'react';

import { BottomBar } from './bottom-bar';
import { ColorEditorSheet } from './color-editor-sheet';
import { ConfirmDialog } from './confirm-dialog';
import { DesignListSheet } from './design-list-sheet';
import { DesignSettingsSheet } from './design-settings-sheet';
import { GoToRowSheet } from './go-to-row-sheet';
import { NewDesignSheet } from './new-design-sheet';
import { QuickGuideSheet } from './quick-guide-sheet';
import { ReassignColorSheet } from './reassign-color-sheet';
import { RowActionsSheet } from './row-actions-sheet';
import { SaveDesignSheet } from './save-design-sheet';
import { TopBar } from './top-bar';
import { TrackingBar } from './tracking-bar';
import { TrackingSummary } from './tracking-summary';

import { useCanvasGestures, type GestureOutput } from '@/hooks/use-canvas-gestures';
import { usePatternSession } from '@/hooks/use-pattern-session';
import { useSavedDesigns } from '@/hooks/use-saved-designs';
import {
  cellsBetween,
  clampCellSize,
  clampOffset,
  fitCellSize,
  hasRowsAbove,
  hasRowsBelow,
  hitTest,
  type CanvasTransform,
  type GridLayout,
} from '@/lib/canvas-geometry';
import { t } from '@/lib/i18n';

const GUTTER_WIDTH = 34;
const DEFAULT_CELL_SIZE = 24;
const DEFAULT_COLUMNS = 11;
const DEFAULT_COLORS = 4;
/** Alto aproximado de las dos franjas, para estimar el lienzo antes de medirlo. */
const BARS_ALLOWANCE = 200;

/** Filas con las que nace un diseño: las que caben en la altura visible (RF-05). */
function initialRowCount(): number {
  const height = typeof window === 'undefined' ? 640 : window.innerHeight;
  const usable = Math.max(160, height - BARS_ALLOWANCE);
  return Math.min(MAX_ROWS, Math.max(MIN_ROWS, Math.floor(usable / DEFAULT_CELL_SIZE)));
}

function newPattern(
  columns = DEFAULT_COLUMNS,
  colorCount = DEFAULT_COLORS,
  patternType: PatternType = DEFAULT_PATTERN_TYPE,
) {
  return createPattern(
    { name: t('untitledDesign'), columns, colorCount, rowCount: initialRowCount(), patternType },
    systemClock,
    uuidGenerator,
  );
}

/** Confirmaciones pendientes. Cada una nombra su consecuencia exacta. */
type Pending =
  | { readonly kind: 'resume-draft'; readonly draft: PatternDraft }
  | {
      readonly kind: 'apply-settings';
      readonly columns: number;
      readonly patternType: PatternType;
      readonly lost: number;
    }
  | { readonly kind: 'raise-color-limit' }
  | { readonly kind: 'discard-for-new' }
  | { readonly kind: 'discard-for-open'; readonly id: EntityId }
  | { readonly kind: 'delete'; readonly summary: PatternSummaryDto }
  | {
      readonly kind: 'replace-on-save';
      readonly name: string;
      readonly existing: PatternSummaryDto;
    }
  | {
      readonly kind: 'replace-on-rename';
      readonly id: EntityId;
      readonly name: string;
      readonly existing: PatternSummaryDto;
    }
  | null;

export interface DesignerScreenProps {
  readonly repository: PatternRepository;
}

export function DesignerScreen({ repository }: DesignerScreenProps) {
  const [session, dispatch] = usePatternSession(repository, createSession(newPattern(), null));
  const designs = useSavedDesigns(repository);

  const { pattern, tool } = session;

  // --- Medición del lienzo ------------------------------------------------
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = React.useState({ width: 360, height: 480 });

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box) setViewport({ width: box.width, height: box.height });
    });

    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, []);

  const staggered = pattern.patternType === 'peyote';

  const layout: GridLayout = React.useMemo(
    () => ({
      columns: pattern.columns,
      rowCount: pattern.rows.length,
      gutterWidth: GUTTER_WIDTH,
      staggered,
    }),
    [pattern.columns, pattern.rows.length, staggered],
  );

  // --- Zoom y desplazamiento ---------------------------------------------
  const [rawTransform, setRawTransform] = React.useState<CanvasTransform>({
    cellSize: DEFAULT_CELL_SIZE,
    offsetX: 0,
    offsetY: 0,
  });

  /**
   * El encuadre válido se **deriva** en cada render en vez de guardarse ya
   * acotado. Así, cuando cambia el tamaño del diseño o el de la pantalla, los
   * límites se recalculan solos: no hace falta un efecto que resincronice
   * estado, que es justo el antipatrón que React desaconseja.
   */
  const transform = React.useMemo(
    () =>
      clampOffset(
        { ...rawTransform, cellSize: clampCellSize(rawTransform.cellSize, layout, viewport) },
        layout,
        viewport,
      ),
    [rawTransform, layout, viewport],
  );

  const applyTransform = React.useCallback((next: CanvasTransform) => {
    setRawTransform(next);
  }, []);

  const fitToScreen = React.useCallback(() => {
    applyTransform({ cellSize: fitCellSize(layout, viewport), offsetX: 0, offsetY: 0 });
  }, [applyTransform, layout, viewport]);

  // --- Paneles y confirmaciones ------------------------------------------
  const [rowMenu, setRowMenu] = React.useState<number | null>(null);
  const [editingColor, setEditingColor] = React.useState<number | null>(null);
  const [reassigning, setReassigning] = React.useState<number | null>(null);
  const [listOpen, setListOpen] = React.useState(false);
  const [newOpen, setNewOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [saveOpen, setSaveOpen] = React.useState(false);
  const [guideOpen, setGuideOpen] = React.useState(false);
  const [pending, setPending] = React.useState<Pending>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  // --- Modo -----------------------------------------------------------------
  /**
   * El modo es estado local de la pantalla y no una acción del reductor: entrar
   * y salir no cambian el patrón, así que el historial de edición no debe
   * enterarse. Con eso RF-05 y RF-06 se cumplen por construcción.
   */
  const [mode, setMode] = React.useState<'design' | 'tracking'>('design');
  const [goToRowOpen, setGoToRowOpen] = React.useState(false);
  const [resetOpen, setResetOpen] = React.useState(false);
  const tracking = mode === 'tracking';

  const status = React.useMemo(() => trackingStatus(pattern), [pattern]);
  const currentReading = React.useMemo(
    () => (status.currentRow === null ? null : readRow(pattern, status.currentRow)),
    [pattern, status.currentRow],
  );

  // --- Borrador ------------------------------------------------------------
  React.useEffect(() => {
    let cancelled = false;

    repository.loadDraft().then(
      (draft) => {
        if (!cancelled && draft) setPending({ kind: 'resume-draft', draft });
      },
      () => {
        // Un borrador ilegible se descarta en silencio.
      },
    );

    return () => {
      cancelled = true;
    };
  }, [repository]);

  // --- Gestos --------------------------------------------------------------
  /**
   * `panned` recuerda que la usuaria movió el lienzo a mano; `framedRow`, cuál
   * fue la última fila encuadrada. Juntas son lo que impide que el autoencuadre
   * y el dedo se peleen (RF-42).
   */
  const panned = React.useRef(false);
  const framedRow = React.useRef<number | null>(null);

  const handleGesture = React.useCallback(
    (output: GestureOutput) => {
      // En seguimiento el lienzo es una guía, no una superficie de dibujo: un
      // dedo deja de pintar y dos siguen desplazando y acercando (RF-08, RF-09).
      if (tracking && output.type !== 'transform') return;

      switch (output.type) {
        case 'tap': {
          // El gutter no pinta: abre las acciones de esa fila.
          if (output.x < GUTTER_WIDTH) {
            const row = Math.floor((output.y - transform.offsetY) / transform.cellSize);
            if (row >= 0 && row < pattern.rows.length) setRowMenu(row);
            return;
          }

          const cell = hitTest(output.x, output.y, layout, transform);
          if (cell) dispatch({ type: 'paint', row: cell.row, column: cell.column });
          return;
        }

        case 'strokeStart':
        case 'strokePaint': {
          const cells = cellsBetween(
            { x: output.fromX, y: output.fromY },
            { x: output.toX, y: output.toY },
            layout,
            transform,
          );
          if (cells.length === 0 && output.type === 'strokePaint') return;
          dispatch({ type: output.type, cells });
          return;
        }

        case 'strokeEnd':
          dispatch({ type: 'strokeEnd' });
          return;

        case 'transform':
          panned.current = true;
          applyTransform({
            cellSize: transform.cellSize * output.scale,
            offsetX: transform.offsetX + output.dx,
            offsetY: transform.offsetY + output.dy,
          });
          return;

        case 'none':
          return;
      }
    },
    [applyTransform, dispatch, layout, pattern.rows.length, tracking, transform],
  );

  const gestureHandlers = useCanvasGestures(handleGesture);

  /**
   * Autoencuadre: al cambiar la fila actual se la trae a la ventana (RF-41).
   *
   * La bandera es lo que hace que no pelee con el dedo. Cuando la usuaria
   * desplaza a mano —para mirar adelante o atrás— el autoencuadre se suprime
   * hasta el siguiente cambio de fila, que es cuando vuelve a tener sentido
   * (RF-42). Sin ella, mirar el patrón sería imposible: cada gesto se
   * desharía solo.
   */
  React.useEffect(() => {
    if (!tracking || status.currentRow === null) return undefined;

    if (framedRow.current !== status.currentRow) {
      framedRow.current = status.currentRow;
      panned.current = false;
    }

    if (panned.current) return undefined;

    const top = status.currentRow * transform.cellSize;
    const visibleTop = -transform.offsetY;
    const visibleBottom = visibleTop + viewport.height - transform.cellSize;

    if (top >= visibleTop && top <= visibleBottom) return undefined;

    // El encuadre se programa para el cuadro siguiente y no se aplica aquí
    // mismo: mover el estado dentro del efecto encadena un render antes de
    // pintar. Es el mismo ciclo en el que el lienzo ya dibuja, así que el
    // desplazamiento y el redibujo caen juntos.
    const frame = window.requestAnimationFrame(() => {
      applyTransform({
        ...transform,
        offsetY: -(top - viewport.height / 2 + transform.cellSize / 2),
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [applyTransform, status.currentRow, tracking, transform, viewport.height]);

  // --- Guardado ------------------------------------------------------------
  const persist = React.useCallback(
    async (name: string) => {
      const saved = await designs.save({ ...pattern, name });
      if (!saved) {
        setNotice(
          designs.lastFailure?.kind === 'quota-exceeded'
            ? t('saveFailedQuota')
            : t('saveFailedUnavailable'),
        );
        return;
      }

      dispatch({ type: 'loaded', pattern: saved, savedFrom: saved.id });
      await repository.clearDraft();
      setSaveOpen(false);
      setNotice(null);
    },
    [designs, dispatch, pattern, repository],
  );

  const requestSave = React.useCallback(
    async (name: string) => {
      const conflict = await designs.findConflict(name, pattern.id);
      if (conflict) {
        setPending({ kind: 'replace-on-save', name, existing: conflict });
        return;
      }
      await persist(name);
    },
    [designs, pattern.id, persist],
  );

  const openDesign = React.useCallback(
    async (id: EntityId) => {
      const loaded = await designs.open(id);
      if (!loaded) {
        setNotice(t('loadFailedCorrupt'));
        return;
      }

      dispatch({ type: 'loaded', pattern: loaded, savedFrom: loaded.id });
      await repository.clearDraft();
      setListOpen(false);
      setNotice(null);
    },
    [designs, dispatch, repository],
  );

  const startNewDesign = React.useCallback(
    (columns: number, colorCount: number, patternType: PatternType) => {
      dispatch({
        type: 'loaded',
        pattern: newPattern(columns, colorCount, patternType),
        savedFrom: null,
      });
      void repository.clearDraft();
      setNewOpen(false);
      setNotice(null);
    },
    [dispatch, repository],
  );

  // --- Resolución de las confirmaciones ------------------------------------
  const confirmPending = React.useCallback(() => {
    if (!pending) return;

    switch (pending.kind) {
      case 'resume-draft':
        dispatch({
          type: 'loaded',
          pattern: pending.draft.pattern,
          savedFrom: pending.draft.savedFrom,
        });
        break;

      case 'discard-for-new':
        setNewOpen(true);
        break;

      case 'apply-settings':
        dispatch({
          type: 'applySettings',
          columns: pending.columns,
          patternType: pending.patternType,
        });
        setSettingsOpen(false);
        break;

      case 'raise-color-limit': {
        // Un solo despacho para que deshacer revierta el acto completo.
        const next = defaultPalette(pattern.palette.colors.length + 1).at(-1);
        if (next) dispatch({ type: 'addColorRaisingLimit', color: next });
        break;
      }

      case 'discard-for-open':
        void openDesign(pending.id);
        break;

      case 'delete':
        void designs.remove(pending.summary.id);
        break;

      case 'replace-on-save':
        void designs.remove(pending.existing.id).then(() => persist(pending.name));
        break;

      case 'replace-on-rename':
        void designs
          .remove(pending.existing.id)
          .then(() => designs.rename(pending.id, pending.name));
        break;
    }

    setPending(null);
  }, [designs, dispatch, openDesign, pattern.palette.colors.length, pending, persist]);

  const cancelPending = React.useCallback(() => {
    if (pending?.kind === 'resume-draft') void repository.clearDraft();
    setPending(null);
  }, [pending, repository]);

  const confirmText = React.useMemo(() => {
    switch (pending?.kind) {
      case 'resume-draft':
        return {
          title: t('resumeDraftTitle'),
          body: t('resumeDraftBody'),
          confirmLabel: t('resume'),
          destructive: false,
        };
      case 'apply-settings':
        return {
          title: t('resizeColumnsTitle'),
          body: t('beadsWillBeLost', { n: pending.lost }),
          confirmLabel: t('confirm'),
          destructive: true,
        };
      case 'raise-color-limit':
        return {
          title: t('colorLimitReached', { n: pattern.palette.declaredCount }),
          body: t('raiseColorLimit', { n: pattern.palette.colors.length + 1 }),
          confirmLabel: t('confirm'),
          destructive: false,
        };
      case 'discard-for-new':
      case 'discard-for-open':
        return {
          title: t('unsavedChangesTitle'),
          body: t('unsavedChangesBody'),
          confirmLabel: t('discardChanges'),
          destructive: true,
        };
      case 'delete':
        return {
          title: t('confirmDeleteTitle'),
          body: t('confirmDeleteBody', { name: pending.summary.name }),
          confirmLabel: t('delete'),
          destructive: true,
        };
      case 'replace-on-save':
      case 'replace-on-rename':
        return {
          title: t('nameTakenTitle'),
          body: t('nameTakenBody', { name: pending.name }),
          confirmLabel: t('replace'),
          destructive: true,
        };
      default:
        return null;
    }
  }, [pattern.palette.colors.length, pattern.palette.declaredCount, pending]);

  // --- Ajustes del diseño ---------------------------------------------------
  /**
   * El ancho y el tejido se aplican con **un solo** despacho (RF-10c): la
   * usuaria pulsó «Aplicar» una vez y deshacer debe revertirlo entero. Con dos
   * acciones haría falta deshacer dos veces lo que hizo de una.
   */
  const handleApplySettings = React.useCallback(
    (columns: number, patternType: PatternType) => {
      const lost = countBeadsOutsideWidth(pattern, columns);

      if (lost > 0) {
        setPending({ kind: 'apply-settings', columns, patternType, lost });
        return;
      }

      dispatch({ type: 'applySettings', columns, patternType });
      setSettingsOpen(false);
    },
    [dispatch, pattern],
  );

  // --- Paleta ---------------------------------------------------------------
  const beadsUsingEditedColor =
    editingColor === null ? 0 : countBeadsUsingColor(pattern, editingColor);

  const handleAddColor = React.useCallback(() => {
    const { colors, declaredCount } = pattern.palette;

    if (colors.length >= MAX_COLORS) {
      setNotice(t('maxColorsReached'));
      return;
    }

    // Alcanzar el tope declarado no esconde el control: se ofrece ampliarlo
    // en el momento (RF-08).
    if (colors.length >= declaredCount) {
      setPending({ kind: 'raise-color-limit' });
      return;
    }

    const next = defaultPalette(colors.length + 1).at(-1);
    if (next) dispatch({ type: 'addColor', color: next });
  }, [dispatch, pattern.palette]);

  return (
    <div className="bg-background text-foreground flex h-[100svh] flex-col overflow-hidden">
      <TopBar
        name={pattern.name}
        dirty={session.dirty}
        tracking={tracking}
        onStartTracking={() => {
          setMode('tracking');
          setRowMenu(null);
        }}
        onExitTracking={() => {
          setMode('design');
        }}
        onOpenList={() => {
          setListOpen(true);
        }}
        onNewDesign={() => {
          if (session.dirty) setPending({ kind: 'discard-for-new' });
          else setNewOpen(true);
        }}
        onOpenSettings={() => {
          setSettingsOpen(true);
        }}
        onSave={() => {
          setSaveOpen(true);
        }}
        onOpenGuide={() => {
          setGuideOpen(true);
        }}
      />

      {notice && (
        <p role="alert" className="bg-destructive/10 text-destructive px-4 py-2 text-sm">
          {notice}
        </p>
      )}

      <div ref={containerRef} className="relative min-h-0 flex-1 overflow-hidden">
        <BeadGridCanvas
          rows={pattern.rows}
          colors={pattern.palette.colors}
          columns={pattern.columns}
          cellSize={transform.cellSize}
          offsetX={transform.offsetX}
          offsetY={transform.offsetY}
          gutterWidth={GUTTER_WIDTH}
          staggered={staggered}
          width={viewport.width}
          height={viewport.height}
          highlightedRow={tracking ? null : rowMenu}
          completedRowCount={tracking ? status.completedRows : 0}
          currentRow={tracking ? status.currentRow : null}
          currentRowRightToLeft={currentReading?.rightToLeft ?? true}
          label={t('canvasLabel')}
          description={t('canvasDescription')}
          {...gestureHandlers}
        />

        <ScrollHint
          showTop={hasRowsAbove(transform)}
          showBottom={hasRowsBelow(layout, transform, viewport)}
          labelTop={t('moreRowsAbove')}
          labelBottom={t('moreRowsBelow')}
        />
      </div>

      {tracking ? (
        <>
          <TrackingSummary
            rowNumber={status.currentRow === null ? null : status.currentRow + 1}
            reading={currentReading}
            colors={pattern.palette.colors}
            alternates={pattern.readingAlternates}
            onToggleAlternates={(alternates) => {
              dispatch({ type: 'setReadingAlternates', alternates });
            }}
          />

          <TrackingBar
            status={status}
            canUndo={session.progressPast.length > 0}
            canRedo={session.progressFuture.length > 0}
            onCompleteRow={() => {
              dispatch({ type: 'completeRow' });
            }}
            onUndo={() => {
              dispatch({ type: 'undoProgress' });
            }}
            onRedo={() => {
              dispatch({ type: 'redoProgress' });
            }}
            onGoToRow={() => {
              setGoToRowOpen(true);
            }}
            onReset={() => {
              setResetOpen(true);
            }}
          />
        </>
      ) : (
        <BottomBar
          palette={pattern.palette}
          tool={tool}
          canUndo={session.past.length > 0}
          canRedo={session.future.length > 0}
          canAddRow={pattern.rows.length < MAX_ROWS}
          onSelectTool={(next) => {
            dispatch({ type: 'selectTool', tool: next });
          }}
          onEditColor={setEditingColor}
          onAddColor={handleAddColor}
          onUndo={() => {
            dispatch({ type: 'undo' });
          }}
          onRedo={() => {
            dispatch({ type: 'redo' });
          }}
          onFitToScreen={fitToScreen}
          onAddRow={() => {
            dispatch({ type: 'appendRow' });
          }}
        />
      )}

      <GoToRowSheet
        open={goToRowOpen}
        totalRows={pattern.rows.length}
        onGo={(row) => {
          dispatch({ type: 'goToRow', row });
        }}
        onOpenChange={setGoToRowOpen}
      />

      {/* Reiniciar borra horas de seguimiento: se confirma, como el resto de
          lo irreversible (RF-19). Reutiliza el diálogo que ya existe. */}
      <ConfirmDialog
        open={resetOpen}
        title={t('trackingResetTitle')}
        body={t('trackingResetBody')}
        confirmLabel={t('confirm')}
        onConfirm={() => {
          dispatch({ type: 'resetProgress' });
          setResetOpen(false);
        }}
        onCancel={() => {
          setResetOpen(false);
        }}
      />

      <RowActionsSheet
        row={rowMenu}
        canRemove={pattern.rows.length > MIN_ROWS}
        canGrow={pattern.rows.length < MAX_ROWS}
        onInsertAbove={() => {
          if (rowMenu !== null) dispatch({ type: 'insertRowAbove', row: rowMenu });
          setRowMenu(null);
        }}
        onDuplicate={() => {
          if (rowMenu !== null) dispatch({ type: 'duplicateRow', row: rowMenu });
          setRowMenu(null);
        }}
        onRemove={() => {
          if (rowMenu !== null) dispatch({ type: 'removeRow', row: rowMenu });
          setRowMenu(null);
        }}
        onClose={() => {
          setRowMenu(null);
        }}
      />

      <ColorEditorSheet
        open={editingColor !== null && reassigning === null}
        index={editingColor}
        color={editingColor === null ? null : (pattern.palette.colors[editingColor] ?? null)}
        beadsUsingColor={beadsUsingEditedColor}
        canRemove={pattern.palette.colors.length > 1}
        onChange={(color: HexColor) => {
          if (editingColor !== null) dispatch({ type: 'updateColor', index: editingColor, color });
        }}
        onRemove={() => {
          if (editingColor === null) return;
          // Sin mostacillas en juego no hay nada que decidir: se quita y ya.
          if (beadsUsingEditedColor === 0) {
            dispatch({ type: 'removeColor', index: editingColor, reassignTo: null });
            setEditingColor(null);
          } else {
            setReassigning(editingColor);
          }
        }}
        onClose={() => {
          setEditingColor(null);
        }}
      />

      <ReassignColorSheet
        open={reassigning !== null}
        palette={pattern.palette}
        removingIndex={reassigning}
        beadCount={reassigning === null ? 0 : countBeadsUsingColor(pattern, reassigning)}
        onReassign={(target) => {
          if (reassigning !== null) {
            dispatch({ type: 'removeColor', index: reassigning, reassignTo: target });
          }
          setReassigning(null);
          setEditingColor(null);
        }}
        onClose={() => {
          setReassigning(null);
        }}
      />

      <DesignSettingsSheet
        open={settingsOpen}
        columns={pattern.columns}
        patternType={pattern.patternType}
        onApply={handleApplySettings}
        onClose={() => {
          setSettingsOpen(false);
        }}
      />

      <QuickGuideSheet
        open={guideOpen}
        onClose={() => {
          setGuideOpen(false);
        }}
      />

      <NewDesignSheet
        open={newOpen}
        onCreate={startNewDesign}
        onClose={() => {
          setNewOpen(false);
        }}
      />

      <SaveDesignSheet
        open={saveOpen}
        initialName={pattern.name}
        onSave={(name) => {
          void requestSave(name);
        }}
        onClose={() => {
          setSaveOpen(false);
        }}
      />

      <DesignListSheet
        open={listOpen}
        designs={designs.designs}
        onOpen={(id) => {
          if (session.dirty) setPending({ kind: 'discard-for-open', id });
          else void openDesign(id);
        }}
        onRename={(id, name) => {
          void designs.findConflict(name, id).then((conflict) => {
            if (conflict) setPending({ kind: 'replace-on-rename', id, name, existing: conflict });
            else void designs.rename(id, name);
          });
        }}
        onDuplicate={(id, name) => {
          void designs.duplicate(id, name);
        }}
        onDelete={(summary) => {
          setPending({ kind: 'delete', summary });
        }}
        onClose={() => {
          setListOpen(false);
        }}
      />

      {confirmText && (
        <ConfirmDialog
          open
          title={confirmText.title}
          body={confirmText.body}
          confirmLabel={confirmText.confirmLabel}
          destructive={confirmText.destructive}
          onConfirm={confirmPending}
          onCancel={cancelPending}
        />
      )}
    </div>
  );
}
